import { farmScreenOff, setSelectedFarm } from '../core/state';
import { navigate } from '../core/router';
import { can, currentRole } from '../core/access';
import { esc, openModal, closeModal, showModalErr } from '../ui/modal';
import { fetchTeams } from '../users/teams-shared';
import { FARM_LOCATION_OPTIONS } from '../core/farm-locations';
import { isDuoFarm, isGeelarkFarm, isAdspowerFarm, isMercadoApiFarm, isHostlessFarm } from '../core/farm-kind';
import { fetchClickerServers } from '../clicker/farm-clicker-config';
import { t } from '../ui/lang';
/**
 * "General" farm-settings tab — rename / delete the farm, its single group, and the
 * per-farm cloud-provider credentials (DuoPlus API key / GeeLark App ID + API key).
 * The group is editable only for a superadmin (a <select> saved via PATCH team_id);
 * for an admin it's inherited/immutable and shown as read-only text.
 */
export function renderGeneralTab(panel, farm) {
    const farmId = farm.id;
    const name = farm.name;
    const screenOffOnConnect = farm.screen_off_on_connect;
    const teamId = farm.team_id ?? null;
    const teamName = farm.team_name ?? null;
    const location = farm.location ?? null;
    const online = farm.online;
    const updateManaged = farm.update_managed ?? false;
    const versionInfo = {
        version: farm.farm_version ?? null,
        latestVersion: farm.latest_version ?? null,
        updateAvailable: farm.update_available ?? false,
    };
    const recordSessions = farm.record_sessions ?? false;
    const recordingSubdir = farm.recording_subdir ?? '';
    const isDuo = isDuoFarm(farm.farm_kind);
    const isGeelark = isGeelarkFarm(farm.farm_kind);
    const duoKeyHint = farm.duo_api_key_hint ?? null;
    const geelarkAppId = farm.geelark_app_id ?? '';
    const geelarkKeyHint = farm.geelark_api_key_hint ?? null;
    const isAdspower = isAdspowerFarm(farm.farm_kind);
    const adspowerApiUrl = farm.adspower_api_url ?? '';
    const adspowerKeyHint = farm.adspower_api_key_hint ?? null;
    const isMercado = isMercadoApiFarm(farm.farm_kind);
    const mercadoServerId = farm.mercado_server_id ?? null;
    const mercadoBucket = farm.mercado_bucket_code ?? '';
    const mercadoPollSec = farm.mercado_poll_sec ?? 5;
    const isSuperadmin = currentRole() === 'superadmin';
    // Host-only sections (version, device behaviour, restart-with-update) don't apply
    // to any agent-less farm — hide them for aggregator, duo AND geelark.
    const hostless = isHostlessFarm(farm.farm_kind);
    // An AdsPower farm has an agent container of its own that recreates itself like a host
    // farm's, so it keeps restart-with-update.
    const canRestartWithUpdate = !hostless || isAdspower;
    // Restart-with-update gating. The button drives the farm agent's docker-managed
    // self-update over the relay, which requires the farm to be online. We gate only
    // on connectivity here; if the farm lacks managed-update support it ACKs with
    // `update-not-managed`, surfaced in the confirm modal as defense-in-depth.
    void updateManaged;
    const updateBlockReason = !online
        ? t('farms.farm_offline')
        : '';
    const updateGateAttrs = updateBlockReason ? ' disabled' : '';
    const updateGateTitle = (base) => esc(updateBlockReason || base);
    const locationOptions = FARM_LOCATION_OPTIONS
        .map((l) => `<option value="${esc(l.code)}" ${(location ?? '') === l.code ? 'selected' : ''}>${l.flag ? `${l.flag} ` : ''}${esc(l.label)}</option>`)
        .join('');
    // Read-only "Version" section: the farm's baked image version, plus an
    // "update available" note when Docker Hub has a newer published tag.
    const versionSection = versionInfo.version && !hostless
        ? `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.version')}</h3>
                <p class="settings-hint">${t('farms.running_version', { version: esc(versionInfo.version) })}${versionInfo.updateAvailable && versionInfo.latestVersion
            ? ` — <span class="farm-version-badge farm-version-badge--update">${t('farms.update_available', { version: esc(versionInfo.latestVersion) })} ↑</span>`
            : ` (${t('farms.latest')})`}</p>
            </div>`
        : '';
    panel.innerHTML = `
        <form class="settings-general" novalidate>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.rename_farm')}</h3>
                <input class="settings-input settings-name" type="text" autocomplete="off" maxlength="100" value="${esc(name)}" required />
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('common.team')}</h3>
                ${isSuperadmin
        ? `<div id="farm-team-wrap"><p class="empty">${t('common.loading')}</p></div>`
        : `<p class="settings-hint">${esc(teamName ?? t('farms.no_team'))}</p>`}
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.location')}</h3>
                <select class="settings-input settings-location">${locationOptions}</select>
                <p class="settings-hint">${t('farms.location_hint')}</p>
            </div>
            ${versionSection}
            ${isDuo ? `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.duo_api_key')}</h3>
                <input class="settings-input settings-duo-key" type="password" autocomplete="off" placeholder="${esc(duoKeyHint ?? t('farms.duo_key_set_ph'))}" />
                <p class="settings-hint">${t('farms.duo_key_hint_settings')}${duoKeyHint ? ` (${esc(duoKeyHint)})` : ''}.</p>
            </div>` : ''}
            ${isGeelark ? `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.geelark_app_id')}</h3>
                <input class="settings-input settings-geelark-appid" type="text" autocomplete="off" maxlength="200" placeholder="${t('farms.geelark_console_ph')}" value="${esc(geelarkAppId)}" />
                <p class="settings-hint">${t('farms.geelark_appid_hint')}</p>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.geelark_api_key')}</h3>
                <input class="settings-input settings-geelark-key" type="password" autocomplete="off" placeholder="${esc(geelarkKeyHint ?? t('farms.geelark_key_set_ph'))}" />
                <p class="settings-hint">${t('farms.geelark_key_hint_settings')}${geelarkKeyHint ? ` (${esc(geelarkKeyHint)})` : ''}.</p>
            </div>` : ''}
            ${isAdspower ? `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.adspower_api_url')}</h3>
                <input class="settings-input settings-adspower-url" type="text" autocomplete="off" maxlength="200" placeholder="http://local.adspower.net:50325" value="${esc(adspowerApiUrl)}" />
                <p class="settings-hint">${t('farms.adspower_api_hint')}</p>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.adspower_api_key')}</h3>
                <input class="settings-input settings-adspower-key" type="password" autocomplete="off" placeholder="${esc(adspowerKeyHint ?? t('farms.adspower_key_set_ph'))}" />
                <p class="settings-hint">${t('farms.adspower_key_hint_settings')}${adspowerKeyHint ? ` (${esc(adspowerKeyHint)})` : ''}.</p>
            </div>` : ''}
            ${isMercado ? `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.mercado_server')}</h3>
                <select class="settings-input settings-mercado-server"><option value="">${t('common.loading')}</option></select>
                <p class="settings-hint">${t('farms.mercado_server_hint')}</p>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.mercado_bucket')}</h3>
                <input class="settings-input settings-mercado-bucket" type="text" autocomplete="off" maxlength="100" value="${esc(mercadoBucket)}" />
                <p class="settings-hint">${t('farms.mercado_bucket_hint')}</p>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.mercado_poll_sec')}</h3>
                <input class="settings-input settings-mercado-poll" type="number" min="3" max="300" step="1" value="${mercadoPollSec}" />
                <p class="settings-hint">${t('farms.mercado_poll_hint')}</p>
            </div>` : ''}
            ${hostless ? '' : `
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.device_behaviour')}</h3>
                <label class="settings-toggle">
                    <input type="checkbox" class="settings-screen-off"${screenOffOnConnect ? ' checked' : ''} />
                    <span>${t('farms.screen_off_label')}</span>
                </label>
                <p class="settings-hint">${t('farms.screen_off_hint')}</p>
            </div>
            <div class="settings-section">
                <h3 class="settings-section__title">${t('farms.session_recording')}</h3>
                <label class="settings-toggle">
                    <input type="checkbox" class="settings-record"${recordSessions ? ' checked' : ''} />
                    <span>${t('farms.record_label')}</span>
                </label>
                <p class="settings-hint">${t('farms.record_hint')}</p>
                <input class="settings-input settings-record-subdir" type="text" autocomplete="off" maxlength="128" placeholder="${t('farms.record_subdir_ph')}" value="${esc(recordingSubdir)}" style="margin-top:8px" />
                <p class="settings-hint">${t('farms.record_subdir_hint')}</p>
            </div>`}
            <div class="settings-section">
                <button type="submit" class="modal__submit settings-save">${t('common.save')}</button>
            </div>
        </form>
        <div class="settings-section">
            <h3 class="settings-section__title">${t('farms.danger_zone')}</h3>
            <p class="settings-hint">${t('farms.danger_hint')}</p>
            <div class="farm-actions">
                ${false && can('farm.reboot') ? `<button type="button" class="modal__submit modal__submit--danger settings-reboot" title="${t('farms.reboot_host_title')}">${t('farms.reboot_host')}</button>` : ''}
                ${can('farm.update') && canRestartWithUpdate ? `<button type="button" class="modal__submit modal__submit--danger settings-update"${updateGateAttrs} title="${updateGateTitle(t('farms.update_title'))}">${t('farms.restart_with_update')}</button>` : ''}
                <button type="button" class="modal__submit modal__submit--danger settings-delete" title="${t('farms.delete_farm_title')}">${t('farms.delete_farm')}</button>
            </div>
        </div>
        <div class="settings-error modal__error" style="display:none"></div>
    `;
    const errorEl = panel.querySelector('.settings-error');
    const showErr = (msg) => { errorEl.textContent = msg; errorEl.style.display = 'block'; };
    // Screen-off + recording toggles are host-only; absent for aggregator/duo (guard reads below).
    const screenOffToggle = panel.querySelector('.settings-screen-off');
    const recordToggle = panel.querySelector('.settings-record');
    const recordSubdirInput = panel.querySelector('.settings-record-subdir');
    const locationSelect = panel.querySelector('.settings-location');
    // Group section (superadmin only): a <select> preselecting the farm's current
    // team_id, included in the unified PATCH as team_id. An admin's group is
    // read-only text above and omitted from the body (server ignores it anyway).
    let teamSelect = null;
    if (isSuperadmin) {
        void (async () => {
            const teamWrap = panel.querySelector('#farm-team-wrap');
            if (!teamWrap)
                return;
            let teams;
            try {
                teams = await fetchTeams();
            }
            catch {
                teamWrap.innerHTML = `<p class="empty">${t('farms.err_load_teams')}</p>`;
                return;
            }
            // A farm of a SOFT-DELETED team keeps that team (delete only hides the group)
            // while the picker lists live ones only — carry the current one in as a flagged
            // option so saving the form doesn't silently un-group the farm.
            const deletedCurrent = teamId !== null && !teams.some((x) => x.id === teamId)
                ? `<option value="${teamId}" selected>${t('users.team_deleted_option', { name: esc(teamName ?? String(teamId)) })}</option>`
                : '';
            const options = deletedCurrent + teams
                .map((team) => `<option value="${team.id}" ${teamId === team.id ? 'selected' : ''}>${esc(team.name)}</option>`)
                .join('');
            teamWrap.innerHTML = `
                <select class="settings-input settings-team-select">
                    <option value="" ${teamId === null ? 'selected' : ''}>${t('farms.no_team')}</option>
                    ${options}
                </select>
            `;
            teamSelect = teamWrap.querySelector('.settings-team-select');
        })();
    }
    // Mercado API farms: the platform server list loads async; the current one stays
    // selected (or "not set" when the farm has none yet).
    const mercadoServerSel = panel.querySelector('.settings-mercado-server');
    if (mercadoServerSel) {
        void fetchClickerServers().then((servers) => {
            mercadoServerSel.innerHTML = `<option value="">${t('farms.mercado_server_none')}</option>` + servers
                .map((s) => `<option value="${s.id}" ${s.id === mercadoServerId ? 'selected' : ''}>${esc(s.name)} — ${esc(s.host)}</option>`)
                .join('');
        });
    }
    const generalForm = panel.querySelector('.settings-general');
    const nameInput = generalForm.querySelector('.settings-name');
    const saveBtn = generalForm.querySelector('.settings-save');
    generalForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        const newName = nameInput.value.trim();
        if (!newName) {
            showErr(t('farms.err_name_required'));
            return;
        }
        if (newName.length > 100) {
            showErr(t('farms.err_name_too_long'));
            return;
        }
        const screenOff = screenOffToggle?.checked ?? screenOffOnConnect;
        const body = {
            name: newName,
            screen_off_on_connect: screenOff,
            location: locationSelect.value || null,
        };
        // Recording settings are host-only — send only when the section is present.
        if (recordToggle) {
            body.record_sessions = recordToggle.checked;
            body.recording_subdir = recordSubdirInput?.value.trim() ?? '';
        }
        if (isSuperadmin && teamSelect) {
            body.team_id = teamSelect.value ? Number.parseInt(teamSelect.value, 10) : null;
        }
        // Duo farms: only send a new API key when the field was filled — blank keeps
        // the stored key (the placeholder shows the masked hint, never a real value).
        const duoKeyInput = generalForm.querySelector('.settings-duo-key');
        if (duoKeyInput && duoKeyInput.value.trim()) {
            body.duo_api_key = duoKeyInput.value.trim();
        }
        // GeeLark farms: same rule for both creds — a filled field replaces the stored
        // value, a blank one keeps it (the key's placeholder is the masked hint only).
        const geelarkAppIdInput = generalForm.querySelector('.settings-geelark-appid');
        if (geelarkAppIdInput) {
            const appId = geelarkAppIdInput.value.trim();
            if (!appId) {
                showErr(t('farms.err_geelark_appid_required'));
                return;
            }
            body.geelark_app_id = appId;
        }
        const geelarkKeyInput = generalForm.querySelector('.settings-geelark-key');
        if (geelarkKeyInput && geelarkKeyInput.value.trim()) {
            body.geelark_api_key = geelarkKeyInput.value.trim();
        }
        // AdsPower farms: the Local API URL is shown raw and always sent (required); the
        // key follows the masked-placeholder rule — a filled field replaces, blank keeps.
        const adspowerUrlInput = generalForm.querySelector('.settings-adspower-url');
        if (adspowerUrlInput) {
            const url = adspowerUrlInput.value.trim();
            if (!url) {
                showErr(t('farms.err_adspower_url_required'));
                return;
            }
            body.adspower_api_url = url;
        }
        const adspowerKeyInput = generalForm.querySelector('.settings-adspower-key');
        if (adspowerKeyInput && adspowerKeyInput.value.trim()) {
            body.adspower_api_key = adspowerKeyInput.value.trim();
        }
        // Mercado API farms: server (blank = not set), bucket and the poll interval (3–300 s).
        if (mercadoServerSel) {
            // Untouched while the list is still loading — don't clear the stored server.
            if (mercadoServerSel.options.length > 1 || mercadoServerSel.value) {
                body.mercado_server_id = mercadoServerSel.value ? Number.parseInt(mercadoServerSel.value, 10) : null;
            }
            body.mercado_bucket_code = generalForm.querySelector('.settings-mercado-bucket')?.value.trim() ?? '';
            const poll = Number.parseInt(generalForm.querySelector('.settings-mercado-poll')?.value ?? '', 10);
            if (!Number.isInteger(poll) || poll < 3 || poll > 300) {
                showErr(t('farms.err_mercado_poll'));
                return;
            }
            body.mercado_poll_sec = poll;
        }
        saveBtn.disabled = true;
        try {
            const r = await fetch(`/api/farms/${farmId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                showErr((await r.json()).error ?? t('farms.err_save_settings'));
                return;
            }
            farmScreenOff.set(newName, screenOff);
            if (newName !== name) {
                // The route is keyed by the stable farm id, so a rename never changes
                // the URL or detaches the device grid; just refresh this settings view
                // (by id) so its title/labels pick up the new name.
                setSelectedFarm(newName, farmId);
                navigate({ page: 'farms', farmId, settings: true });
                return;
            }
            saveBtn.textContent = `✓ ${t('common.saved')}`;
            setTimeout(() => { saveBtn.textContent = t('common.save'); }, 1500);
        }
        catch {
            showErr(t('farms.err_network'));
        }
        finally {
            saveBtn.disabled = false;
        }
    });
    // Reboot host (admin and above, gated by can('farm.reboot')): a confirm modal (mirrors showDeleteFarmConfirm)
    // fires POST /api/farms/:id/reboot. The farm drops off the relay and reconnects
    // on its own, so we don't block the UI — just report the outcome and close.
    const rebootBtn = panel.querySelector('.settings-reboot');
    rebootBtn?.addEventListener('click', () => {
        const triggerBtn = rebootBtn;
        const modal = openModal(`
            <h2 class="modal__title">${t('farms.reboot_host')}</h2>
            <p class="modal__text">${t('farms.reboot_confirm', { name: esc(name) })}</p>
            <div id="fr-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="fr-cancel">${t('common.cancel')}</button>
                <button type="button" class="modal__submit modal__submit--danger" id="fr-confirm">${t('farms.reboot_host')}</button>
            </div>
        `);
        const modalErr = modal.querySelector('#fr-error');
        modal.querySelector('#fr-cancel').addEventListener('click', closeModal);
        modal.querySelector('#fr-confirm').addEventListener('click', async () => {
            const confirmBtn = modal.querySelector('#fr-confirm');
            confirmBtn.disabled = true;
            try {
                const r = await fetch(`/api/farms/${farmId}/reboot`, { method: 'POST' });
                if (!r.ok) {
                    const data = await r.json().catch(() => ({}));
                    showModalErr(modalErr, data.error ?? t('farms.err_reboot_failed'));
                    confirmBtn.disabled = false;
                    return;
                }
                closeModal();
                // The farm reboots and reconnects on its own — don't block the UI.
                // Flash a transient confirmation on the trigger button (mirrors the
                // "✓ Saved" pattern on the Save button above).
                triggerBtn.disabled = true;
                triggerBtn.textContent = `✓ ${t('farms.reboot_sent')}`;
                setTimeout(() => { triggerBtn.disabled = false; triggerBtn.textContent = t('farms.reboot_host'); }, 3000);
            }
            catch {
                showModalErr(modalErr, t('farms.err_network'));
                confirmBtn.disabled = false;
            }
        });
    });
    // Restart with update (admin and above, gated by can('farm.update')):
    // drives the farm agent's docker-managed self-update over the relay via
    // POST /api/farms/:id/update with { pull }. `pull:true` recreates with a fresh
    // image. The endpoint returns the farm's
    // ACK { ok, reason }: on ok we report the restart and the farm reconnects on its
    // own; on !ok we surface the machine-readable reason. A confirm modal mirrors the
    // reboot confirm above.
    const wireUpdateButton = (btn, opts) => {
        btn?.addEventListener('click', () => {
            const triggerBtn = btn;
            const modal = openModal(`
                <h2 class="modal__title">${esc(opts.title)}</h2>
                <p class="modal__text">${opts.text}</p>
                <div id="fu-error" class="modal__error" style="display:none"></div>
                <div class="modal__actions">
                    <button type="button" class="modal__cancel" id="fu-cancel">${t('common.cancel')}</button>
                    <button type="button" class="modal__submit modal__submit--danger" id="fu-confirm">${esc(opts.title)}</button>
                </div>
            `);
            const modalErr = modal.querySelector('#fu-error');
            modal.querySelector('#fu-cancel').addEventListener('click', closeModal);
            modal.querySelector('#fu-confirm').addEventListener('click', async () => {
                const confirmBtn = modal.querySelector('#fu-confirm');
                confirmBtn.disabled = true;
                try {
                    const r = await fetch(`/api/farms/${farmId}/update`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ pull: opts.pull, relayHost: window.location.origin }),
                    });
                    if (!r.ok) {
                        const data = await r.json().catch(() => ({}));
                        const msg = r.status === 404 ? t('farms.farm_offline') : (data.error ?? t('farms.err_restart_failed'));
                        showModalErr(modalErr, msg);
                        confirmBtn.disabled = false;
                        return;
                    }
                    const ack = await r.json().catch(() => ({}));
                    if (!ack.ok) {
                        // Machine-readable failure from the farm. Map the known reasons
                        // to a clear, translated sentence instead of showing the code.
                        const reasonMsg = ack.reason === 'update-not-managed'
                            ? t('farms.err_update_not_managed')
                            : ack.reason === 'spawn-failed'
                                ? t('farms.err_spawn_failed')
                                : `${t('farms.err_restart_rejected')}${ack.reason ? ` (${esc(ack.reason)})` : ''}.`;
                        showModalErr(modalErr, reasonMsg);
                        confirmBtn.disabled = false;
                        return;
                    }
                    closeModal();
                    // Recreate kicked off — the farm drops and reconnects on its own.
                    // Don't block the UI; flash a transient confirmation on the button.
                    triggerBtn.disabled = true;
                    triggerBtn.textContent = `✓ ${t('farms.farm_restarting')}`;
                    setTimeout(() => { triggerBtn.disabled = false; triggerBtn.textContent = opts.label; }, 3000);
                }
                catch {
                    showModalErr(modalErr, t('farms.err_network'));
                    confirmBtn.disabled = false;
                }
            });
        });
    };
    wireUpdateButton(panel.querySelector('.settings-update'), {
        pull: true,
        label: t('farms.restart_with_update'),
        title: t('farms.restart_with_update'),
        text: t('farms.update_confirm', { name: esc(name) }),
    });
    const deleteBtn = panel.querySelector('.settings-delete');
    let armed = false;
    deleteBtn.addEventListener('click', async () => {
        if (!armed) {
            armed = true;
            deleteBtn.textContent = t('farms.click_again_delete');
            setTimeout(() => { armed = false; deleteBtn.textContent = t('farms.delete_farm'); }, 4000);
            return;
        }
        deleteBtn.disabled = true;
        errorEl.style.display = 'none';
        try {
            const r = await fetch(`/api/farms/${farmId}`, { method: 'DELETE' });
            if (!r.ok) {
                showErr((await r.json()).error ?? t('farms.err_delete_failed'));
                deleteBtn.disabled = false;
                return;
            }
            navigate({ page: 'farms' });
        }
        catch {
            showErr(t('farms.err_network'));
            deleteBtn.disabled = false;
        }
    });
}
